import PublicLayout from "@/components/PublicLayout";
import { useSEO } from "@/lib/useSEO";

export default function Terms() {
  useSEO({
    title: "Terms of Use — InsureGenie India",
    description: "Terms and conditions for using InsureGenie, India's AI insurance advisory platform. Educational content only, not a substitute for licensed advice.",
  });
  return (
    <PublicLayout eyebrow="Legal" title="Terms & Conditions.">
      <p className="text-muted-foreground"><em>Last updated: February 2026</em></p>

      <p>
        These Terms govern your use of InsureGenie (the &quot;Service&quot;). By creating an account
        or using the Service, you agree to these Terms. If you do not agree, please do not use the Service.
      </p>

      <h2>1. Eligibility</h2>
      <p>
        You must be 18 years or older and legally capable of entering into a binding contract to use the Service.
      </p>

      <h2>2. Nature of the Service</h2>
      <p>
        InsureGenie is an <strong>educational AI assistant</strong>. It provides information about insurance
        concepts, policies, and tax benefits drawn from publicly available sources and AI reasoning.
        InsureGenie is <strong>not</strong> an insurance company, broker, agent, aggregator, financial advisor,
        tax consultant, or legal advisor.
      </p>

      <h2>3. No advice</h2>
      <p>
        Responses generated by InsureGenie are for general information only. They do not constitute insurance
        advice, financial advice, legal advice, or tax advice. You should consult qualified professionals before
        purchasing any insurance product or making any financial decision.
      </p>

      <h2>4. Accuracy</h2>
      <p>
        While we work hard to keep our reasoning accurate and our citations honest, AI-generated content can
        contain errors, omissions, or out-of-date information. Policy features, premiums, and regulations change
        frequently. You are responsible for verifying any material fact with the relevant insurer, IRDAI, or
        professional advisor before acting on it.
      </p>

      <h2>5. Your account</h2>
      <ul>
        <li>You are responsible for safeguarding your login credentials.</li>
        <li>You agree not to share your account or impersonate another person.</li>
        <li>We may suspend or terminate accounts that violate these Terms or applicable law.</li>
      </ul>

      <h2>6. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service for unlawful, fraudulent, or harmful purposes.</li>
        <li>Attempt to reverse-engineer, scrape, or overload the Service.</li>
        <li>Upload malware, copyrighted material you don&apos;t own, or sensitive personal data of third parties.</li>
        <li>Use the Service to give insurance, tax, or financial advice to others as if it were professional advice.</li>
      </ul>

      <h2>7. Intellectual property</h2>
      <p>
        InsureGenie, its branding, code, and design are owned by us. You retain ownership of the content you
        upload. By uploading documents, you grant us a non-exclusive license to process them within the Service
        for your benefit (parsing, summarizing, indexing).
      </p>

      <h2>8. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, InsureGenie and its operators shall not be liable for any
        indirect, incidental, consequential, or punitive damages arising from your use of the Service. Our
        total liability for any claim shall not exceed INR 1,000 or the amount you have paid us in the preceding
        12 months, whichever is greater.
      </p>

      <h2>9. Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless InsureGenie from any third-party claim arising out of your
        misuse of the Service or violation of these Terms.
      </p>

      <h2>10. Termination</h2>
      <p>
        You may close your account at any time. We may suspend or terminate access if you violate these Terms,
        applicable law, or for security reasons.
      </p>

      <h2>11. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. We will notify registered users of material changes inside
        the app. Continued use after a change constitutes acceptance.
      </p>

      <h2>12. Governing law</h2>
      <p>
        These Terms are governed by the laws of India. Any dispute shall be subject to the exclusive jurisdiction
        of the courts of Bengaluru, India.
      </p>

      <h2>13. Contact</h2>
      <p>
        Questions about these Terms? Write to <a href="mailto:legal@insuregenie.ai">legal@insuregenie.ai</a> or
        use our <a href="/contact">contact form</a>.
      </p>
    </PublicLayout>
  );
}
